import React, { ButtonHTMLAttributes, ReactNode, MouseEvent } from "react";
import styled, { css } from "styled-components";
import { isMobile } from "react-device-detect";

// カラーの定数
const COLORS = {
  PRIMARY: "#009ace",
  PRIMARY_HOVER: "#0076a5",
  SECONDARY: "#a7a8a9",
  SECONDARY_HOVER: "#009ace",
  TERTIARY: "#f0f0f0",
  TERTIARY_HOVER: "#a7a8a9",
  DISABLED: "#a7a8a9",
  WHITE: "#ffffff",
};

// サイズの定数
const SIZES = {
  large: {
    pc: { padding: "12px 24px", minWidth: "192px" },
    sp: { padding: "12px 24px", minWidth: "192px" },
  },
  medium: {
    pc: { padding: "8px 24px", minWidth: "192px" },
    sp: { padding: "8px 16px", minWidth: "148px" },
  },
  small: {
    pc: { padding: "4px 12px", minWidth: "128px" },
    sp: { padding: "4px 12px", minWidth: "128px" },
  },
};

// テーマの定義
const THEMES: {
  [theme in "primary" | "secondary" | "tertiary"]: {
    [variant in "filled" | "outlined" | "text"]: {
      default: {
        color: string;
        backgroundColor?: string;
        borderColor?: string;
        boxShadow?: string;
      };
      hover: {
        color?: string;
        backgroundColor?: string;
        borderColor?: string;
        boxShadow?: string;
      };
    };
  };
} = {
  primary: {
    filled: {
      default: {
        color: COLORS.WHITE,
        backgroundColor: COLORS.PRIMARY,
        borderColor: COLORS.PRIMARY,
      },
      hover: {
        backgroundColor: COLORS.PRIMARY_HOVER,
        borderColor: COLORS.PRIMARY_HOVER,
        boxShadow: "2px 4px 8px 3px rgba(0, 154, 206, 0.25)",
      },
    },
    outlined: {
      default: {
        color: COLORS.PRIMARY,
        backgroundColor: "transparent",
        borderColor: COLORS.PRIMARY,
      },
      hover: {
        color: COLORS.PRIMARY_HOVER,
        borderColor: COLORS.PRIMARY_HOVER,
        backgroundColor: `${COLORS.PRIMARY}10`,
        boxShadow: "2px 4px 8px 3px rgba(0, 154, 206, 0.1)",
      },
    },
    text: {
      default: {
        color: COLORS.PRIMARY,
      },
      hover: {
        color: COLORS.PRIMARY_HOVER,
      },
    },
  },
  secondary: {
    filled: {
      default: {
        color: COLORS.WHITE,
        backgroundColor: COLORS.SECONDARY,
        borderColor: COLORS.SECONDARY,
      },
      hover: {
        backgroundColor: COLORS.SECONDARY_HOVER,
        borderColor: COLORS.SECONDARY_HOVER,
        boxShadow: "2px 4px 8px 3px rgba(0, 0, 0, 0.1)",
      },
    },
    outlined: {
      default: {
        color: COLORS.SECONDARY,
        backgroundColor: "transparent",
        borderColor: COLORS.SECONDARY,
      },
      hover: {
        color: COLORS.SECONDARY_HOVER,
        borderColor: COLORS.SECONDARY_HOVER,
        backgroundColor: `${COLORS.SECONDARY}10`,
        boxShadow: "2px 4px 8px 3px rgba(0, 0, 0, 0.05)",
      },
    },
    text: {
      default: {
        color: COLORS.SECONDARY,
      },
      hover: {
        color: COLORS.SECONDARY_HOVER,
      },
    },
  },
  tertiary: {
    filled: {
      default: {
        color: COLORS.SECONDARY,
        backgroundColor: COLORS.TERTIARY,
        borderColor: COLORS.TERTIARY,
      },
      hover: {
        color: COLORS.WHITE,
        backgroundColor: COLORS.TERTIARY_HOVER,
        borderColor: COLORS.TERTIARY_HOVER,
        boxShadow: "2px 4px 8px 3px rgba(0, 0, 0, 0.05)",
      },
    },
    outlined: {
      default: {
        color: COLORS.SECONDARY,
        backgroundColor: "transparent",
        borderColor: COLORS.TERTIARY,
      },
      hover: {
        borderColor: COLORS.SECONDARY,
        backgroundColor: `${COLORS.TERTIARY}30`,
        boxShadow: "2px 4px 8px 3px rgba(0, 0, 0, 0.03)",
      },
    },
    text: {
      default: {
        color: COLORS.SECONDARY,
      },
      hover: {
        color: COLORS.PRIMARY,
      },
    },
  },
};

// スタイル付きコンポーネント用のprops型定義
type StyledButtonProps = {
  $size: "large" | "medium" | "small";
  $theme: "primary" | "secondary" | "tertiary";
  $variant: "filled" | "outlined" | "text";
  $width: "default" | "full" | "fit";
  $iconOnly: boolean;
  disabled?: boolean;
};

type IconProps = {
  $variant: "filled" | "outlined" | "text";
};

// スタイル付きコンポーネントの定義
const StyledButton = styled.button.attrs<StyledButtonProps>(({ $size }) => {
  // サイズに応じたフォントクラスを設定
  let fontClasses = '';
  if ($size === 'large') {
    fontClasses = 'heading-spacing-18 u-line-height-200';
  } else if ($size === 'medium') {
    fontClasses = 'heading-16 u-line-height-200';
  } else if ($size === 'small') {
    fontClasses = 'heading-13 u-line-height-200';
  }
  
  return {
    className: fontClasses
  };
})<StyledButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 4px;
  border: 1px solid transparent;
  transition: all 0.2s ease;
  white-space: nowrap;
  cursor: pointer;
  
  /* 幅の設定 */
  ${({ $size, $width }) => {
    const sizeObj = SIZES[$size][isMobile ? "sp" : "pc"];
    
    if ($width === "full") {
      return css`
        width: 100%;
        padding: ${sizeObj.padding};
      `;
    } else if ($width === "fit") {
      return css`
        width: auto;
        min-width: auto;
        padding: ${sizeObj.padding};
      `;
    } else {
      return css`
        width: auto;
        min-width: ${sizeObj.minWidth};
        padding: ${sizeObj.padding};
      `;
    }
  }}
  
  /* バリアントとテーマの設定 */
  ${({ $variant, $theme, disabled }) => {
    if (disabled) {
      return css`
        color: ${COLORS.SECONDARY};
        background-color: ${COLORS.TERTIARY};
        border-color: ${COLORS.TERTIARY};
        cursor: not-allowed;
        box-shadow: none;
      `;
    }
    
    const themeObj = THEMES[$theme][$variant];
    
    if ($variant === "text") {
      return css`
        background-color: transparent;
        border: none;
        padding: 0;
        color: ${themeObj.default.color};

        &:hover {
          color: ${themeObj.hover.color};
        }
      `;
    } else if ($variant === "outlined" || $variant === "filled") {
      return css`
        color: ${themeObj.default.color};
        background-color: ${themeObj.default.backgroundColor};
        border-color: ${themeObj.default.borderColor};

        &:hover {
          color: ${themeObj.hover.color || themeObj.default.color};
          background-color: ${themeObj.hover.backgroundColor || themeObj.default.backgroundColor};
          border-color: ${themeObj.hover.borderColor || themeObj.default.borderColor};
          box-shadow: ${themeObj.hover.boxShadow || "none"};
        }
      `;
    }
  }}
  
  /* アイコンのみの場合の調整 */
  ${({ $iconOnly }) => $iconOnly && css`
    aspect-ratio: 1 / 1;
    min-width: unset;
    width: auto;
    padding: 0.5em;
  `}
  
  /* フォーカス状態のアクセシビリティ対応 */
  &:focus-visible {
    outline: 2px solid ${COLORS.PRIMARY};
    outline-offset: 2px;
  }
`;

// アイコンコンポーネント
// 左右アイコンは optical bounding が含まれるため、配置側で margin: -4px を
// 当てて見た目のバランスを取る（ラベルとの隙間は gap: 8px のまま）。
const Icon = styled.span<IconProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $variant }) => $variant === "text" ? "24px" : "24px"};
  color: inherit;

  &:first-child {
    margin-left: -4px;
  }

  &:last-child {
    margin-right: -4px;
  }
`;

// propsの型定義
export type ButtonProps = {
  children?: ReactNode;
  size?: "large" | "medium" | "small";
  theme?: "primary" | "secondary" | "tertiary";
  variant?: "filled" | "outlined" | "text";
  width?: "default" | "full" | "fit";
  leftIcon?: string;
  rightIcon?: string;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  className?: string;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type" | "onClick" | "className">;

/**
 * ボタンコンポーネント
 *
 * 運用ルール（docs/ui-foundation/03-global-components.md「Button の 3 階層ルール」）:
 * - `variant` は "filled"（既定）に統一する。"outlined" / "text" は非推奨
 *   （新規使用禁止。既存箇所は触ったタイミングで filled + 適切な theme へ置き換える）
 * - 役割は theme で表す: 必須 = "primary" / 任意 = "secondary" /
 *   キャンセル・戻る等の対 = "tertiary"
 *
 * @param {Object} props - コンポーネントのプロパティ
 * @param {React.ReactNode} props.children - ボタンのテキストまたは内容
 * @param {string} props.size - ボタンのサイズ（"large", "medium", "small"）
 * @param {string} props.theme - ボタンのテーマ（"primary", "secondary", "tertiary"）
 * @param {string} props.variant - ボタンのバリアント（"filled" 推奨。"outlined", "text" は非推奨）
 * @param {string} props.width - ボタンの幅設定（"default", "full", "fit"）
 * @param {string} props.leftIcon - 左側のアイコン名（Material Symbols）
 * @param {string} props.rightIcon - 右側のアイコン名（Material Symbols）
 * @param {boolean} props.disabled - 無効状態かどうか
 * @param {string} props.type - ボタンのタイプ（"button", "submit", "reset"）
 * @param {Function} props.onClick - クリック時のコールバック関数
 * @param {string} props.className - 追加のCSSクラス名
 */
const Button = ({
  children,
  size = "medium",
  theme = "primary",
  variant = "filled",
  width = "default",
  leftIcon,
  rightIcon,
  disabled = false,
  type = "button",
  onClick,
  className = "",
  ...props
}: ButtonProps) => {
  const iconOnly = Boolean(!children && (leftIcon || rightIcon));
  
  // テキストバリアントの場合は自動的にwidth="fit"に
  const effectiveWidth = variant === "text" ? "fit" : width;
  
  return (
    <StyledButton
      $size={size}
      $theme={theme}
      $variant={variant}
      disabled={disabled}
      $width={effectiveWidth}
      $iconOnly={iconOnly}
      type={type}
      onClick={disabled ? undefined : onClick}
      className={className}
      {...props}
    >
      {leftIcon && (
        <Icon $variant={variant} className="material-symbols-rounded">
          {leftIcon}
        </Icon>
      )}
      
      {children}
      
      {rightIcon && (
        <Icon $variant={variant} className="material-symbols-rounded">
          {rightIcon}
        </Icon>
      )}
    </StyledButton>
  );
};

export default React.memo(Button);
